// ============================================================================
// Preview as signer — /money/envelopes/:envelopeId/preview
//
// Renders the REAL signer component with preview={true}, rather than a
// lookalike. That is the whole point: DocuSign's own preview diverges from
// what the signer gets, most visibly on prefilled values, and a preview you
// cannot trust is worse than no preview. Here the sender sees the same
// consent gate, the same field tabs, the same adopt modal and the same
// prefilled values, because it is the same code.
//
// Every side effect is short-circuited by the preview flag: no consent event,
// no signature, no audit rows, no email.
// ============================================================================

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { EnvSignerMeta, EnvStatus, envStatus } from './envelope-lib';
import { EnvelopeSignerFlow } from './envelope-signer';

/**
 * Build the exact meta shape the signer flow receives, from the sender-side
 * envelope status. Anything the signer would get from their token is filled
 * with the recipient's real values so the preview is faithful.
 */
function toSignerMeta(
  data: EnvStatus,
  recipientId: string | null,
): EnvSignerMeta | null {
  const signers = data.recipients.filter((r) => r.role !== 'CC');
  const me = signers.find((r) => r.recipient_id === recipientId) ?? signers[0];
  if (!me) return null;
  const env = data.envelope;
  const expiry = new Date();
  expiry.setDate(expiry.getDate() + (env.expires_in_days ?? 14));

  return {
    scope: 'envelope',
    state: 'verified',
    envelope_id: env.envelope_id,
    title: env.title,
    envelope_status: env.status,
    recipient_role: me.role,
    recipient_status: me.status,
    signing_order: me.signing_order,
    current_group: me.signing_order,
    my_turn: true,
    signer_email_masked: me.email,
    signer_name: me.name,
    verified: true,
    otp_pending: false,
    note_to_signer: env.note_to_signer ?? null,
    sender_name: env.sender_name ?? 'Terminal Cleaning Services',
    expires_at: expiry.toISOString(),
    consented: false,
    disclosure_version: null,
    auth_mode: 'emailed_link',
    fields: data.fields
      .filter((f) => f.recipient_id === me.recipient_id)
      .map((f) => ({
        field_id: f.field_id,
        field_type: f.field_type,
        page: f.page,
        position_x: f.position_x,
        position_y: f.position_y,
        width: f.width,
        height: f.height,
        required: f.required,
        value: f.value,
      })),
  };
}

export function EnvelopePreviewPage() {
  const { envelopeId = '' } = useParams<{ envelopeId: string }>();
  const [params, setParams] = useSearchParams();
  const recipientId = params.get('recipient');

  const [data, setData] = useState<EnvStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    envStatus(envelopeId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [envelopeId]);

  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-st-danger-fg">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-tcs-text-muted" aria-label="Loading" />
      </div>
    );
  }

  const signers = data.recipients.filter((r) => r.role !== 'CC');
  const meta = toSignerMeta(data, recipientId);

  return (
    <div className="min-h-screen bg-tcs-surface">
      <div className="flex flex-wrap items-center gap-3 border-b border-tcs-border bg-tcs-raised px-4 py-2">
        <Link
          to={`/money/envelopes/${envelopeId}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-tcs-accent-ink"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to prep
        </Link>
        {signers.length > 1 && (
          <label className="ml-auto flex items-center gap-2 text-xs text-tcs-text-muted">
            Preview as
            <select
              value={recipientId ?? signers[0]?.recipient_id ?? ''}
              onChange={(e) => setParams({ recipient: e.target.value })}
              className="rounded-md border border-tcs-border px-2 py-1.5 text-sm text-tcs-text"
            >
              {signers.map((r) => (
                <option key={r.recipient_id} value={r.recipient_id}>
                  {r.name || r.email} ({r.role.toLowerCase()})
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {meta ? (
        <EnvelopeSignerFlow token="" meta={meta} preview />
      ) : (
        <div className="mx-auto max-w-2xl p-6 text-center">
          <p className="text-sm text-tcs-text-muted">
            Add a signer to this envelope before previewing — there is nothing
            for a signer to see yet.
          </p>
        </div>
      )}
    </div>
  );
}
